// Punchlines for the Reverse Setup exercise
// Given a punchline, write a setup that leads to it
// Uses seeded shuffle to ensure no repeats until full list is cycled

/**
 * Seeded pseudo-random number generator (mulberry32).
 * Returns a function that produces deterministic floats in [0, 1) for a given seed.
 */
function seededRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Returns a shuffled copy of the punchlines using a deterministic seed.
 * Same seed always produces the same order.
 */
function shufflePunchlines(seed: number): string[] {
  const lines = [...PUNCHLINES];
  const rand = seededRng(seed);
  for (let i = lines.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [lines[i], lines[j]] = [lines[j], lines[i]];
  }
  return lines;
}

/**
 * Get a deterministic punchline based on the current cycle.
 * Each user globally sees the same punchline for the same day,
 * and lines never repeat until the full list is cycled.
 */
export function getRandomPunchline(daysSinceEpoch: number = 0): string {
  const totalLines = PUNCHLINES.length;
  const cycle = Math.floor(Math.abs(daysSinceEpoch) / totalLines);
  const positionInCycle = Math.abs(daysSinceEpoch) % totalLines;
  const shuffled = shufflePunchlines(cycle + 1);
  return shuffled[positionInCycle];
}

export const PUNCHLINES: string[] = [
  "...but apparently I'm not supposed to microwave it.",
  "...so now my therapist has a therapist.",
  "...but the judge said I can't go back to that Costco.",
  "...turns out my passport photo is also my mugshot.",
  "...which is weird because I don't even have a driver's license.",
  "...but my dentist said I have a 'distinctive' smile.",
  "...and that's why I'm banned from Home Depot.",
  "...but the airline said that wasn't a 'real' emotional support animal.",
  "...which explains why my mom still won't return my calls.",
  "...so now my LinkedIn has a warning label.",
  "...but apparently that's illegal in all 50 states.",
  "...which is why I get weird looks at family dinners.",
  "...and that's the last time I volunteer for anything.",
  "...but the HR department wrote it down and everything.",
  "...turns out Costco has a long memory.",
  "...which is why I can't watch TV with my grandma anymore.",
  "...but the DMV lady said she'd seen worse.",
  "...so now I'm a 'person of interest' to the HOA.",
  "...and my Uber rating dropped significantly.",
  "...but the bartender said he's heard weirder.",
  "...which is why I don't do group projects anymore.",
  "...so I switched to a new dermatologist who doesn't ask questions.",
  "...but apparently there's a Facebook group dedicated to me now.",
  "...and that's why I use self-checkout exclusively.",
  "...turns out LinkedIn has a reporting feature.",
  "...which explains the restraining order.",
  "...but the TSA agent just laughed and let me through.",
  "...so now I'm the cautionary tale at family gatherings.",
  "...and my credit score somehow got worse.",
  "...but the jury voted 11-1, so I almost made it.",
  "...which is why I'm not invited to potlucks anymore.",
  "...so my dog gives me the side-eye now.",
  "...but apparently that's 'not appropriate workplace behavior.'",
  "...and my landlord texted me the code change without explanation.",
  "...turns out there's a specific section in the lease about that.",
  "...which is why my GPS now avoids certain roads.",
  "...but the store manager just shook her head and walked away.",
  "...so now I'm a cautionary tale at their staff meetings.",
  "...and my therapist literally wrote 'fascinating' in her notes.",
  "...but the 911 operator recognized my voice immediately.",
];
